<script setup lang="ts">
import type { ReportFilters } from '~/models/ReportFilters'
import type { ContentDetailsAssignment, ContentDetailsStatistics, ContentDetailsTest, ReportContentVisit, Statistics } from '~/models/Report'
import type { GroupMember } from '~/models/Group'
import type { BaseItem } from '~/models/Content/BaseItem'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import useProductStore from '~/stores/product'
import { UserRole } from '~/models/User/UserRole'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useGradeString from '~/composables/useGradeString'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'

const props = defineProps<{
  content: BaseItem
  groupMembers?: GroupMember[]
  visit?: ReportContentVisit
  statistics?: Statistics | ContentDetailsStatistics
  filters?: ReportFilters
  tasks?: ContentDetailsTest[]
  assignments?: ContentDetailsAssignment[]
}>()
const { t } = useI18n()
const { createGradeString } = useGradeString()
const { getProductFromResource } = useProductStore()
const { buildResourceUrl } = useUrlBuilder()

const product = computed(() => getProductFromResource(props.content))

const contentUrl = computed(() => buildResourceUrl(props.content))

const subjectOrProductName = computed(() => {
  if (!product.value)
    return ''
  return product.value.subjects?.length ? t(`metadata.subjects.${product.value.subjects[0]}`) : ''
})

const subtitle = computed(() => {
  const subject = subjectOrProductName.value
  const grades = createGradeString(props.content.grades)
  const parent = props.content.parentLocationName

  return [subject, grades, parent].filter(Boolean).join(' / ')
})

const isContentDetailsStatistics = (stats: Statistics | ContentDetailsStatistics): stats is ContentDetailsStatistics => {
  return 'totalContentActivities' in stats
}

const isLegacyStatistics = (stats: Statistics | ContentDetailsStatistics): stats is Statistics => {
  return 'assignmentPrcComplete' in stats
}

const hasAssignments = computed(() => {
  if (props.assignments !== undefined) {
    return props.assignments.length > 0
  }

  if (!props.statistics)
    return false

  if (isLegacyStatistics(props.statistics)) {
    return props.statistics.assignmentPrcComplete !== undefined && props.statistics.assignmentPrcComplete !== null
  }

  if (isContentDetailsStatistics(props.statistics)) {
    return props.statistics.prcSubmittedAssignments !== undefined && props.statistics.prcSubmittedAssignments !== null
  }

  return false
})

const hasTasks = computed(() => {
  if (props.tasks !== undefined) {
    return props.tasks.length > 0
  }

  if (!props.statistics)
    return false

  if (isLegacyStatistics(props.statistics)) {
    return props.statistics.flytPrcCorrect !== undefined && props.statistics.flytPrcCorrect !== null
  }

  if (isContentDetailsStatistics(props.statistics)) {
    return props.statistics.prcSubmittedTests !== undefined && props.statistics.prcSubmittedTests !== null
  }

  return false
})

const assignmentPercentage = computed(() => {
  if (!props.statistics)
    return 0

  if (isLegacyStatistics(props.statistics)) {
    return props.statistics.assignmentPrcComplete || 0
  }

  if (isContentDetailsStatistics(props.statistics)) {
    return props.statistics.prcSubmittedAssignments || 0
  }

  return 0
})

const taskPercentage = computed(() => {
  if (!props.statistics)
    return 0

  if (isLegacyStatistics(props.statistics)) {
    return props.statistics.flytPrcCorrect || 0
  }

  if (isContentDetailsStatistics(props.statistics)) {
    return props.statistics.prcSubmittedTests || 0
  }

  return 0
})

const visitStats = computed(() => {
  if (props.statistics && isContentDetailsStatistics(props.statistics) && props.groupMembers) {
    const students = props.groupMembers.filter(member => member.role !== UserRole.Teacher)
    const totalStudents = students.length
    const visitedCount = props.statistics.totalContentActivities

    const percentage = totalStudents > 0 ? Math.round((visitedCount / totalStudents) * 100) : 0
    const progressWidth = `${percentage}%`

    return {
      visitedCount,
      totalStudents,
      percentage,
      progressWidth
    }
  }

  if (props.statistics && isLegacyStatistics(props.statistics) && props.statistics.contentVisits) {
    const { amountVisited, amountInGroup } = props.statistics.contentVisits
    const percentage = amountInGroup > 0 ? Math.round((amountVisited / amountInGroup) * 100) : 0
    const progressWidth = `${percentage}%`

    return {
      visitedCount: amountVisited,
      totalStudents: amountInGroup,
      percentage,
      progressWidth
    }
  }

  if (!props.groupMembers || !props.visit) {
    return {
      visitedCount: 0,
      totalStudents: 0,
      percentage: 0,
      progressWidth: '0%'
    }
  }

  const students = props.groupMembers.filter(member => member.role !== UserRole.Teacher)
  const totalStudents = students.length

  if (totalStudents === 0) {
    return {
      visitedCount: 0,
      totalStudents: 0,
      percentage: 0,
      progressWidth: '0%'
    }
  }

  const visitedUserIds = new Set<string>()
  const visitedCount = students.filter(student => visitedUserIds.has(student.userId)).length

  const percentage = Math.round((visitedCount / totalStudents) * 100)
  const progressWidth = `${percentage}%`

  return {
    visitedCount,
    totalStudents,
    percentage,
    progressWidth
  }
})

const labelText = computed(() => t(`labels.${props.content.label || props.content.contentTypeIdentifier}`))
const assignmentPercentageText = computed(() => `${assignmentPercentage.value}%`)
const taskPercentageText = computed(() => `${taskPercentage.value}%`)
const visitStatsText = computed(() => `${visitStats.value.visitedCount}/${visitStats.value.totalStudents}`)
const groupText = computed(() => `${t('reports.sidebar.group')}: ${props.filters?.selectedGroup?.name}`)
</script>

<template>
  <article class="border-green-40 w-full overflow-hidden rounded-md border bg-white">
    <div class="p-4 md:p-6">
      <div class="mb-6 flex flex-col gap-4 md:flex-row">
        <div class="flex justify-start">
          <div class="bg-gray-5 relative h-32 w-32 shrink-0 overflow-hidden rounded-md">
            <img
              v-if="props.content.thumbnail"
              :src="props.content.thumbnail"
              :alt="props.content.thumbnailAlt"
              class="size-full object-cover"
              loading="lazy"
            >
          </div>
        </div>
        <div class="flex min-w-0 flex-1 flex-col">
          <div class="mb-2">
            <RouterLink
              :to="contentUrl"
              class="hover:text-seagreen-60 focus:text-seagreen-60 inline-block rounded-sm transition-colors duration-200 focus-visible:ring"
            >
              <h2 class="text-green-40 text-lg leading-tight font-extrabold underline transition-colors hover:text-green-50 sm:text-xl" v-text="props.content.title" />
            </RouterLink>
          </div>

          <div class="mb-2 flex flex-wrap items-center gap-2">
            <span
              v-if="props.content.label"
              class="bg-seagreen-60 text-seagreen-5 rounded-md px-2 py-1 text-xs font-semibold sm:px-3 sm:py-1.5 sm:text-sm"
              v-text="labelText"
            />
            <span
              v-if="subjectOrProductName"
              class="bg-seagreen-60 text-seagreen-5 rounded-md px-2 py-1 text-xs font-semibold sm:px-3 sm:py-1.5 sm:text-sm"
              v-text="subjectOrProductName"
            />
          </div>

          <p
            v-if="!!subtitle"
            class="text-green-40 mb-2 text-xs sm:text-sm"
            v-text="subtitle"
          />

          <p
            v-if="props.filters?.selectedGroup?.name"
            class="text-green-40 mb-2 text-xs sm:text-sm"
            v-text="groupText"
          />

          <RichTextRenderer
            v-if="props.content.intro"
            :text="props.content.intro"
            class="text-gray-70 line-clamp-2 text-sm sm:line-clamp-3 sm:text-base"
          />
        </div>
      </div>

      <div class="grid grid-cols-1 gap-4" :class="{ 'lg:grid-cols-2 lg:gap-6': hasAssignments || hasTasks }">
        <div v-if="hasAssignments || hasTasks">
          <h3 class="text-green-40 mb-3 text-base font-semibold" v-text="t('reports.metrics.completedTasksAndAssignments')" />
          <div class="space-y-3">
            <div v-if="hasAssignments" class="space-y-2">
              <div class="flex items-center justify-between">
                <span class="text-green-40 text-sm font-medium" v-text="t('reports.metrics.assignments')" />
                <span class="text-green-40 text-sm" v-text="assignmentPercentageText" />
              </div>
              <div class="bg-gray-10 h-3 w-full overflow-hidden rounded-full">
                <div
                  class="bg-green-40 h-full rounded-full transition-all duration-300"
                  :style="{ width: `${assignmentPercentage}%` }"
                />
              </div>
            </div>

            <div v-if="hasTasks" class="space-y-2">
              <div class="flex items-center justify-between">
                <span class="text-green-40 text-sm font-medium" v-text="t('reports.metrics.tasks')" />
                <span class="text-green-40 text-sm" v-text="taskPercentageText" />
              </div>
              <div class="bg-gray-10 h-3 w-full overflow-hidden rounded-full">
                <div
                  class="bg-green-40 h-full rounded-full transition-all duration-300"
                  :style="{ width: `${taskPercentage}%` }"
                />
              </div>
            </div>
          </div>
        </div>

        <div>
          <h3 class="text-green-40 mb-3 text-base font-semibold" v-text="t('reports.metrics.visitedArticle')" />
          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <span class="text-green-40 text-sm font-medium" v-text="t('reports.metrics.visits')" />
              <span class="text-green-40 text-sm" v-text="visitStatsText" />
            </div>
            <div class="bg-gray-10 h-3 w-full overflow-hidden rounded-full">
              <div
                class="bg-green-40 h-full rounded-full transition-all duration-300"
                :style="{ width: visitStats.progressWidth }"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>
